import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { EarningsStats } from "@/components/host/earnings/EarningsStats";
import { EarningsAnalytics } from "@/components/host/earnings/EarningsAnalytics";
import { PayoutRequests } from "@/components/host/earnings/PayoutRequests";
import { WalletSection } from "@/components/host/earnings/WalletSection";
import { TransactionsTable } from "@/components/host/earnings/TransactionsTable";
import { PlatformConnections } from "@/components/host/settings/PlatformConnections";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const HostEarningsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [payoutAmount, setPayoutAmount] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState<{
    is_verified: boolean;
    government_id_submitted: boolean;
    address_proof_submitted: boolean;
  } | null>(null);

  const { data: earningsData } = useQuery({
    queryKey: ['host-earnings-stats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: hostPayments, error: paymentsError } = await supabase
        .from('host_guest_payments')
        .select('*')
        .eq('host_id', user.id);

      if (paymentsError) throw paymentsError;

      const totalEarnings = hostPayments?.reduce((sum, payment) => sum + Number(payment.amount), 0) || 0;
      const pendingPayments = hostPayments?.filter(p => p.status === 'pending')
        .reduce((sum, payment) => sum + Number(payment.amount), 0) || 0;
      const completedPayments = hostPayments?.filter(p => p.status === 'completed')
        .reduce((sum, payment) => sum + Number(payment.amount), 0) || 0;
      const commissionsTotal = hostPayments?.reduce((sum, payment) => sum + Number(payment.commission_amount), 0) || 0;

      return {
        totalEarnings,
        pendingPayments,
        completedPayments,
        commissionsTotal
      };
    }
  });

  const { data: payoutRequests = [] } = useQuery({
    queryKey: ['host-payout-requests'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('host_payout_requests')
        .select('*')
        .eq('host_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ['host-transactions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const handlePayoutRequest = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('host_payout_requests')
        .insert({
          host_id: user.id,
          amount: payoutAmount,
          status: 'pending',
          provider: 'mtn' // Default provider
        });

      if (error) throw error;

      toast({
        title: "Payout request submitted",
        description: "Your payout request has been submitted successfully.",
      });

      setPayoutAmount(0);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit payout request. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        let { data: verificationData, error: verificationError } = await supabase
          .from('host_verification_requirements')
          .select('*')
          .eq('host_id', user.id)
          .maybeSingle();

        if (verificationError) throw verificationError;

        // If no verification record exists, create one
        if (!verificationData) {
          const { data: newVerification, error: createError } = await supabase
            .from('host_verification_requirements')
            .insert({
              host_id: user.id,
              government_id_submitted: false,
              address_proof_submitted: false,
              phone_verified: false,
              email_verified: false
            })
            .select()
            .single();

          if (createError) throw createError;
          verificationData = newVerification;
        }

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_verified')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        setVerificationStatus({
          is_verified: profileData.is_verified,
          government_id_submitted: verificationData?.government_id_submitted || false,
          address_proof_submitted: verificationData?.address_proof_submitted || false,
        });
      } catch (error) {
        console.error('Error fetching verification status:', error);
      }
    };

    fetchVerificationStatus();
  }, []);

  const renderVerificationAlert = () => {
    if (!verificationStatus) return null;

    if (verificationStatus.is_verified) {
      return (
        <Alert className="mb-6 bg-green-50">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Verification Complete</AlertTitle>
          <AlertDescription>
            Your host account is fully verified. You can now receive payments and manage your listings.
          </AlertDescription>
        </Alert>
      );
    }

    if (!verificationStatus.government_id_submitted || !verificationStatus.address_proof_submitted) {
      return (
        <Alert className="mb-6" variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Verification Required</AlertTitle>
          <AlertDescription>
            Please complete your host verification to enable all earnings features.
            <Button 
              variant="outline" 
              className="mt-2"
              onClick={() => navigate('/host/verification')}
            >
              Complete Verification
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Verification Pending</AlertTitle>
        <AlertDescription>
          Your verification documents are being reviewed. This usually takes 1-2 business days.
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold mb-6">Earnings & Payouts</h1>
      
      {renderVerificationAlert()}

      <div className="grid gap-6 md:grid-cols-2">
        <EarningsStats stats={earningsData || {
          totalEarnings: 0,
          pendingPayments: 0,
          completedPayments: 0,
          commissionsTotal: 0
        }} />
        <WalletSection />
      </div>

      <EarningsAnalytics />

      {verificationStatus?.is_verified && (
        <>
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Platform Connections</h2>
            <PlatformConnections />
          </Card>

          <PayoutRequests 
            payoutRequests={payoutRequests}
            payoutAmount={payoutAmount}
            onPayoutAmountChange={setPayoutAmount}
            onPayoutRequest={handlePayoutRequest}
          />
          <TransactionsTable transactions={transactions.map(t => ({
            ...t,
            commission_amount: 0 // Add missing field required by Transaction type
          }))} />
        </>
      )}
    </div>
  );
};

export default HostEarningsPage;
